import React, {FC, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";
import {Link} from "react-router-dom";
import AddressName from "../../../common/address-name";
import BTooltip from "../../../../components/b-tooltip";
import BPrice from "../../../common/b-price";
import {formatNumber, toXDCNumber, truncate} from "../../../../common/utils/function";
import useCommon from "../../../../common/hooks/useCommon";
import TokenImage from "../../../common/token-image";

interface TransferProps {
    list: any[];
    label?: any;
    transferType: string;
}

const TokenTransfers: FC<TransferProps> = (props) => {
    const {t} = useTranslation();
    const {get0x} = useCommon();
    const _tokenType = useSelector((e: Object) => (e as any)?.common?.tokenType);
    const [tab, setTab] = useState("all");

    const netTransfers = useMemo(() => {
        const transfers: { [key: string]: { sent: number; received: number; token: any; address: any } } = {};

        props.list.forEach((data: any) => {
            const fromAddress = data.from.hash;
            const toAddress = data.to.hash;
            const value = data?.total?.value ? parseFloat(toXDCNumber(data?.total?.value, data?.total?.decimals ?? 6, 4)) : 0;

            if (!transfers[fromAddress]) {
                transfers[fromAddress] = { sent: 0, received: 0, token: data.token, address: data.from };
            }
            transfers[fromAddress].sent += value;

            if (!transfers[toAddress]) {
                transfers[toAddress] = { sent: 0, received: 0, token: data.token, address: data.to };
            }
            transfers[toAddress].received += value;
            if (fromAddress === toAddress) {
                transfers[fromAddress].sent -= value; 
            }
        });

        const output: any[] = [];
        Object.entries(transfers).forEach(([address, { sent, received, token }]) => {
            if (sent > 0) {
                output.push({ address, value: sent, token, type: 'sent' });
            }
            if (received > 0) {
                output.push({ address, value: received, token, type: 'received' });
            }
        });

        return output;
    }, [props?.list]);
        
    if ([`${_tokenType}-20`, `${_tokenType}-404`].includes(props?.transferType)) console.log("=>(token-transfers.tsx:59) netTransfers", netTransfers);
    if (!props?.list?.length) return null;

    return (
        <React.Fragment>
            <div className="bs-list-row">
                <div className="bs-list-title">
                    <BTooltip text={`${t("List of")} ${props?.transferType} ${t("tokens transferred in the transaction.")}`}>
                        <i className="bi bi-question-circle me-2"></i>
                    </BTooltip>
                    {props?.label}:
                </div>
                <div className="bs-list-content">
                    {[`${_tokenType}-20`, `${_tokenType}-404`].includes(props?.transferType) && (
                        <div className="bs-tab">
                            <button className={`bs-tab-item ${tab === 'all' ? "active" : ""}`} onClick={() => setTab("all")}>{t("All Transfers")}</button>
                            <button className={`bs-tab-item ${tab === 'net' ? "active" : ""}`} onClick={() => setTab("net")}>{t("Net Transfers")}</button>
                        </div>
                    )}
                    <div className="bs-tab-content">
                        {tab === 'all' && (
                            <React.Fragment>
                                {props?.list.map((data: any, index: number) => (
                                    <div className="d-flex flex-wrap align-items-center gap-2 mt-1 mb-2" key={index}>
                                        {([`${_tokenType === "ERC" ? "ERC" : "ERC"}-20`].includes(data?.token?.type)) ? null :
                                            [`${_tokenType}-721`, `${_tokenType}-1155`, `${_tokenType}-404`, 'Mint', 'Burnt'].includes(props?.transferType) && (
                                                <Link to={`/token/${get0x(data?.token?.address)}`} className="content-center bg-light border rounded p-1">
                                                    <TokenImage image={data?.token?.icon_url} nft symbol={data?.token?.symbol} params={{width: 32, height: 32}}/>
                                                </Link>
                                            )}
                                        <div>
                                            {([`${_tokenType === "ERC" ? "ERC" : "ERC"}-20`].includes(data?.token?.type)) ? null :
                                                [`${_tokenType}-721`, `${_tokenType}-1155`, `${_tokenType}-404`, 'Mint', 'Burnt'].includes(props?.transferType) && (
                                                    <div className="d-flex align-items-center">
                                                        {props?.transferType} {t("Token ID")} &nbsp; [
                                                        <Link to={`/nft/${get0x(data?.token?.address)}/${data?.total?.token_id}`} className="d-flex align-items-center">
                                                            {truncate(data?.total?.token_id)}
                                                        </Link>
                                                        ] &nbsp;
                                                        <Link to={`/token/${get0x(data?.token?.address)}`} className="d-flex align-items-center">
                                                            <TokenImage image={data?.token?.icon_url} symbol={data?.token?.symbol} params={{width: 20, className: "me-1"}}/>
                                                            {data?.token?.name} ({data?.token?.symbol})
                                                        </Link>
                                                    </div>
                                                )}
                                            <div className="d-flex mb-1">
                                                <i className="bi bi-caret-right-fill gray"></i>
                                                <div className="d-inline-flex flex-wrap align-items-center mb-1">
                                                    <span className="fw-medium text-black">{t("From")}</span>
                                                    {props?.transferType === 'Mint' && (
                                                        <span style={{marginLeft: ".35rem"}}>⛏️</span>)}
                                                    <Link to={`/address/${get0x(data?.from?.hash)}`}><AddressName data={data?.from}/></Link>
                                                    <span className="fw-medium text-black">{t("To")}</span>
                                                    {props?.transferType === 'Burnt' && (
                                                        <span className="me-1" style={{marginLeft: ".35rem"}}>🔥</span>
                                                    )}
                                                    <Link to={`/address/${get0x(data?.to?.hash)}`}><AddressName data={data?.to}/></Link>
                                                    {[`${_tokenType}-20`, `${_tokenType}-404`, 'Burnt', 'Mint'].includes(props?.transferType) && (
                                                        <React.Fragment>
                                                            <span className="fw-medium text-black me-1">{t("For")}</span> <span
                                                            className="text-black me-1"> {toXDCNumber(data?.total?.value, data?.total?.decimals ?? 6, 18)}</span>
                                                            <span className="me-1">
                                                                <BPrice
                                                                    value={data?.total?.value}
                                                                    coingeckoID={data?.token?.coingecko_id}
                                                                    timestamp={data?.timestamp}
                                                                    XDCUSD={data?.token?.exchange_rate}
                                                                    decimal={data?.total?.decimals ?? 6}
                                                                    type=""
                                                                />
                                                            </span>
                                                            <Link to={`/token/${get0x(data?.token?.address)}`} className="d-flex align-items-center">
                                                                <TokenImage image={data?.token?.icon_url} symbol={data?.token?.symbol} params={{width: 20, className: "me-1"}}/>
                                                                {data?.token?.name}
                                                            </Link>
                                                            <span className="gray">&nbsp;({data?.token?.symbol})</span>
                                                        </React.Fragment>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </React.Fragment>
                        )}
                    {tab === 'net' && (
                        <React.Fragment>
                            {netTransfers.map((data: any, index: number) => (
                                <div className="d-flex flex-wrap align-items-center gap-2 mt-1 mb-2" key={index}>
                                    <Link to={`/address/${get0x(data.address)}`}>
                                        <AddressName data={{ hash: data.address }} />
                                    </Link>
                                    <span className="fw-medium text-black">{data.type}</span>
                                    <span className="me-1">{formatNumber(data.value, 4)}</span>
                                    <Link to={`/token/${get0x(data.token.address)}`} className="d-flex align-items-center">
                                        <TokenImage image={data.token.icon_url} symbol={data.token.symbol} params={{ width: 20, className: "me-1" }} />
                                        {data.token.name} ({data.token.symbol})
                                    </Link>
                                </div>
                            ))}
                        </React.Fragment>
                    )}
                    </div>
                </div>
            </div>
            <hr/>
        </React.Fragment>
    );
};

export default TokenTransfers;
